import { useEffect, useRef, useState } from 'react';
import { Bug, Lightbulb, Mail, MessageSquareMore, X } from 'lucide-react';

const FEEDBACK_EMAIL = 'muhammad86762@gmail.com';
const DISMISSED_KEY = 'ortl-feedback-prompt-dismissed';

function shouldOpenPrompt() {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(DISMISSED_KEY) !== 'true';
  } catch {
    return true;
  }
}

const mailtoUrl = `mailto:${FEEDBACK_EMAIL}?subject=${encodeURIComponent('Ortl beta feedback')}&body=${encodeURIComponent(`Hi Ortl team,

Feedback type: Bug / Improvement / Suggestion

What happened or what would you like to see?


Page or feature:


Thanks!`)}`;

export function FeedbackPrompt({ theme }: { theme: 'dark' | 'light' }) {
  const [isOpen, setIsOpen] = useState(shouldOpenPrompt);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISSED_KEY, 'true');
    } catch {
      // The prompt can still be dismissed when storage is unavailable.
    }
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;

    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss();
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
      previousFocus?.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return <div
    className={`feedback-layer theme-${theme}`}
    onMouseDown={(event) => { if (event.currentTarget === event.target) dismiss(); }}
  >
    <section className="feedback-dialog" role="dialog" aria-modal="true" aria-labelledby="feedback-title" aria-describedby="feedback-description">
      <header className="feedback-dialog-header">
        <div className="feedback-dialog-label"><MessageSquareMore size={14} /> ORTL BETA FEEDBACK</div>
        <button ref={closeButtonRef} type="button" onClick={dismiss} aria-label="Close feedback prompt"><X size={17} /></button>
      </header>
      <div className="feedback-dialog-body">
        <span className="feedback-status"><i /> HELP US IMPROVE THE WORKBENCH</span>
        <h2 id="feedback-title">Found something we should know about?</h2>
        <p id="feedback-description">Report a bug, suggest an improvement, or share an idea. Your feedback helps shape Ortl while it is in beta.</p>
        <div className="feedback-types" aria-label="Accepted feedback types">
          <span><Bug size={14} /> Bugs</span>
          <span><Lightbulb size={14} /> Improvements</span>
          <span><MessageSquareMore size={14} /> Suggestions</span>
        </div>
        <div className="feedback-email-row"><Mail size={14} /><span>Feedback goes directly to</span><a href={`mailto:${FEEDBACK_EMAIL}`}>{FEEDBACK_EMAIL}</a></div>
      </div>
      <footer className="feedback-dialog-actions">
        <button type="button" className="feedback-later" onClick={dismiss}>Maybe later</button>
        <a className="feedback-send" href={mailtoUrl} onClick={dismiss}><Mail size={14} /> Send feedback</a>
      </footer>
    </section>
  </div>;
}
